// This will show the user the community locations and ask them
// how many boxes per location exist, the address and the special
// instructions.  It will then call and create a string that is
// sent to be downloaded into a word file.
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Button from "@material-ui/core/Button";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Checkbox from "@material-ui/core/Checkbox";
import Box from "@material-ui/core/Box";
import TextField from "@material-ui/core/TextField";
import CreateDownloadLabelsString from "../../../../components/dialogs/functions/CreateDownloadLabelsString.js";
import useCommunityLocations from "../../../../functions/Firestore/useCommunityLocations.js";
import LoadingContent from "../../../../components/LoadingContent.jsx";

// This is the dialog that show the list of community hubs to select from.
function PrintOutLabelsBox({ onClose, open, downloadString, handleDownload }) {
  const handleClose = () => {
    onClose(false);
  };
  //  Holds the locations that have been selected to print and the number
  // of boxes the location has.  {locationName : integer}
  const [selectedLocations, setSelectedLocations] = useState({});
  // This holds the data of the user inputed info for the address and the
  // additional comments. {locationsName : {additionalComments : "", address : ""}}
  const [locationDetailsMap, setLocationDetailsMap] = useState({});
  // This creates an array to list the location names excluding the on
  // site location.
  const communityLocationNames = Object.keys(downloadString).filter(
    (name) => name !== "Pickup On Site",
  );
  // Loads from the database the community location's data.
  const { locationData, loading } = useCommunityLocations(
    communityLocationNames,
  );
  // This updates locationDetailsMap to have the default values for each location.
  useEffect(() => {
    if (Array.isArray(locationData)) {
      const locationDetailsMapTemp = { ...locationDetailsMap };
      locationData.forEach((loc) => {
        locationDetailsMapTemp[loc.locationName] = loc;
        locationDetailsMapTemp[loc.locationName] = {};
        locationDetailsMapTemp[loc.locationName].additionalComments =
          loc.additionalComments;
        locationDetailsMapTemp[loc.locationName].address = loc.address;
      });

      setLocationDetailsMap({ ...locationDetailsMapTemp });
    }
  }, [locationData]);

  // Starts to download process by creating the string and then sending
  // it to the download function.
  const handleStartDownload = () => {
    const locationLabelsStringTemp = CreateDownloadLabelsString(
      selectedLocations,
      locationDetailsMap,
    );
    handleDownload(locationLabelsStringTemp, null, "Box-Labels");
  };

  // Updates which locations are selected to be printed.
  const handleToggle = (location) => {
    setSelectedLocations((prev) => {
      const newSelected = { ...prev };
      if (location in newSelected) {
        delete newSelected[location];
      } else {
        newSelected[location] = 1; // default number when selected
      }
      return newSelected;
    });
  };

  // Changes the number of boxes per location.
  const handleNumberChange = (location, value) => {
    const number = parseInt(value, 10);
    if (!isNaN(number)) {
      setSelectedLocations((prev) => ({
        ...prev,
        [location]: number,
      }));
    }
  };
  // Updates the text values of the address and the additionalComments.
  const handleDropOffInstructionsChange = (location, name, value) => {
    const locationDetailsMapTemp = { ...locationDetailsMap };
    locationDetailsMapTemp[location][name] = value;
    setLocationDetailsMap({ ...locationDetailsMapTemp });
  };

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="simple-dialog-title"
      open={open}
    >
      <DialogTitle id="simple-dialog-title">
        Please Select the Locations and Their Box Count
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <LoadingContent inline={true} />
        ) : (
          <Box display="flex" flexDirection="column">
            {communityLocationNames.map((pickupLocation) => (
              <Box
                key={pickupLocation}
                display="flex"
                flexDirection="column"
                border="1px solid #ccc"
                borderRadius="8px"
                padding="12px"
                marginBottom="10px"
              >
                <Box display="flex" alignItems="center">
                  <Checkbox
                    checked={pickupLocation in selectedLocations}
                    onChange={() => handleToggle(pickupLocation)}
                    name={pickupLocation}
                  />
                  <Box fontWeight="bold">{pickupLocation}</Box>
                </Box>

                {pickupLocation in selectedLocations && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    paddingLeft="32px"
                    gap={2}
                    mt={1}
                  >
                    <TextField
                      type="number"
                      label="Number of Labels"
                      value={selectedLocations[pickupLocation]}
                      onChange={(e) =>
                        handleNumberChange(pickupLocation, e.target.value)
                      }
                      inputProps={{ min: 1 }}
                      style={{ width: "100px" }}
                    />
                    <TextField
                      label="Location Address"
                      value={locationDetailsMap[pickupLocation]?.address || ""}
                      onChange={(e) =>
                        handleDropOffInstructionsChange(
                          pickupLocation,
                          "address",
                          e.target.value,
                        )
                      }
                      style={{ width: "100%" }}
                    />
                    <TextField
                      label="Drop Off Instructions"
                      value={
                        locationDetailsMap[pickupLocation]
                          ?.additionalComments || ""
                      }
                      onChange={(e) =>
                        handleDropOffInstructionsChange(
                          pickupLocation,
                          "additionalComments",
                          e.target.value,
                        )
                      }
                      style={{ width: "100%" }}
                      multiline
                      maxRows={4}
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={handleClose}>
          CLOSE
        </Button>
        {Object.keys(selectedLocations).length > 0 && (
          <Button color="primary" onClick={() => handleStartDownload()}>
            DOWNLOAD
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

PrintOutLabelsBox.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  selectedDate: PropTypes.string.isRequired,
  distributionLocation: PropTypes.string.isRequired,
  downloadString: PropTypes.string.isRequired,
  downloadGridString: PropTypes.string.isRequired,
};

export default function PrintOutLabelsDialog({
  downloadString,
  handleDownload,
}) {
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div>
      <Button
        onClick={handleClickOpen}
        style={{
          marginBottom: "10px",
          border: "1px solid #2196f3",
          borderRadius: "5px",
          color: "#2196f3",
          padding: "10px 20px",
          width: "100%",
        }}
      >
        LOCATION LABELS
      </Button>

      <PrintOutLabelsBox
        open={open}
        onClose={handleClose}
        downloadString={downloadString}
        handleDownload={handleDownload}
      />
    </div>
  );
}
